/**
 * Style Mapper Utilities
 *
 * Extracts and maps Figma style properties to web-friendly format.
 */

import type {
  BlurEffect,
  BorderProperties,
  BorderRadiusProperties,
  ShadowEffect,
  StyleProperties,
} from '../types/component-tree.js'
import { figmaColorToRGB, resolveColors, rgbToHex } from './color-resolver.js'

/**
 * Extract border properties from Figma stroke properties
 * @param props - Figma node properties
 */
export function extractBorder(props: any): BorderProperties | undefined {
  if (!props.strokePaints || props.strokePaints.length === 0) {
    return undefined
  }

  const border: BorderProperties = {
    style: 'solid', // Figma doesn't have dashed in clipboard format (at least not commonly)
  }

  const color = resolveColors(props.strokePaints)
  if (color) border.color = color
  if (props.strokeWeight !== undefined) border.width = props.strokeWeight

  return border
}

/**
 * Extract border radius from Figma rectangle corner radius properties
 */
export function extractBorderRadius(props: any): BorderRadiusProperties | number | undefined {
  const tl = props.rectangleTopLeftCornerRadius
  const tr = props.rectangleTopRightCornerRadius
  const bl = props.rectangleBottomLeftCornerRadius
  const br = props.rectangleBottomRightCornerRadius

  // Check if any radius is defined
  if (tl === undefined && tr === undefined && bl === undefined && br === undefined) {
    return undefined
  }

  // Uniform radius (all corners the same)
  if (tl === tr && tr === bl && bl === br && tl !== undefined) {
    return tl
  }

  // Individual corners
  return {
    topLeft: tl,
    topRight: tr,
    bottomLeft: bl,
    bottomRight: br,
  }
}

/**
 * Extract shadow effects from Figma effects array
 * @param effects - Figma effects array
 */
export function extractShadows(
  effects: any[] | undefined,
): ShadowEffect[] | undefined {
  if (!effects || effects.length === 0) {
    return undefined
  }

  const shadows = effects
    .filter((effect) => effect.type === 'DROP_SHADOW' || effect.type === 'INNER_SHADOW')
    .map((effect): ShadowEffect => {
      const rgbColor = figmaColorToRGB(effect.color)
      return {
        type: effect.type === 'DROP_SHADOW' ? 'drop-shadow' : 'inner-shadow',
        color: rgbToHex(rgbColor),
        offset: {
          x: effect.offset?.x ?? 0,
          y: effect.offset?.y ?? 0,
        },
        blur: effect.radius ?? 0,
        spread: effect.spread ?? 0,
      }
    })

  return shadows.length > 0 ? shadows : undefined
}

/**
 * Extract blur effect from Figma effects array
 */
export function extractBlur(effects: any[] | undefined): BlurEffect | undefined {
  if (!effects || effects.length === 0) {
    return undefined
  }

  const blurEffect = effects.find(
    (effect) => effect.type === 'LAYER_BLUR' || effect.type === 'BACKGROUND_BLUR',
  )

  if (!blurEffect) {
    return undefined
  }

  return {
    type: blurEffect.type === 'LAYER_BLUR' ? 'layer-blur' : 'background-blur',
    radius: blurEffect.radius ?? 0,
  }
}

/**
 * Extract complete style properties from Figma node
 * @param props - Figma node properties
 */
export function extractStyle(props: any): StyleProperties | undefined {
  // Check if node has any style properties
  const hasBackground = props.fillPaints && props.fillPaints.length > 0
  const hasBorder = props.strokePaints && props.strokePaints.length > 0
  const hasRadius =
    props.rectangleTopLeftCornerRadius !== undefined ||
    props.rectangleTopRightCornerRadius !== undefined ||
    props.rectangleBottomLeftCornerRadius !== undefined ||
    props.rectangleBottomRightCornerRadius !== undefined
  const hasEffects = props.effects && props.effects.length > 0
  const hasOpacity = props.opacity !== undefined && props.opacity !== 1
  const hasVisibility = props.visible === false

  if (!hasBackground && !hasBorder && !hasRadius && !hasEffects && !hasOpacity && !hasVisibility) {
    return undefined
  }

  const style: StyleProperties = {}

  // Background/fill
  if (hasBackground) {
    const background = resolveColors(props.fillPaints)
    if (background) style.background = background
  }

  // Border
  if (hasBorder) {
    const border = extractBorder(props)
    if (border) style.border = border
  }

  // Border radius
  if (hasRadius) {
    const radius = extractBorderRadius(props)
    if (radius !== undefined) style.radius = radius
  }

  // Effects
  if (hasEffects) {
    const shadow = extractShadows(props.effects)
    if (shadow) style.shadow = shadow
    const blur = extractBlur(props.effects)
    if (blur) style.blur = blur
  }

  // Opacity
  if (hasOpacity) {
    style.opacity = props.opacity
  }

  // Visibility
  if (hasVisibility) {
    style.visible = props.visible
  }

  return style
}
